import { ForgotPasswordForm } from "@/components/PasswordReset";
import { useState } from "react";
import { Loader2, Mail, Lock, User, Sparkles, ArrowLeft } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { KlyrLogo } from "@/components/MarketingSite";
import { toast } from "sonner";

export const AuthScreen = ({ onAuthed, initialMode = "login", onBack }) => {
  const [mode, setMode] = useState(initialMode); // login | register | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user =
        mode === "login"
          ? await api.login({ email, password })
          : await api.register({ email, password, name, ref: localStorage.getItem("klyr_ref") || "" });
      if (mode === "register") localStorage.removeItem("klyr_ref");
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      onAuthed(user);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    try {
      const cfg = await api.googleConfig();
      if (cfg?.enabled && cfg?.client_id) {
        const redirectUrl = window.location.origin + "/auth/google";
        const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem("g_oauth_state", state);
        const p = new URLSearchParams({
          client_id: cfg.client_id,
          redirect_uri: redirectUrl,
          response_type: "code",
          scope: "openid email profile",
          state,
          access_type: "online",
          prompt: "select_account",
          include_granted_scopes: "true",
        });
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${p.toString()}`;
        return;
      }
    } catch (e) {
      /* fall through to Emergent-managed login */
    }
    // Fallback: Emergent-managed Google login (if own client not configured)
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls =
    "w-full bg-[#050915]/70 border border-gold-500/20 focus:border-gold-400/70 outline-none rounded-xl pl-10 pr-4 py-3 font-ui text-sm text-slate-100 placeholder:text-slate-600 transition-colors";

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-10 bg-[#050915] overflow-hidden">
      {/* cinematic gold light bleed */}
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[680px] h-[680px] rounded-full" aria-hidden
        style={{ background: "radial-gradient(ellipse at center, rgba(229,193,88,0.18) 0%, rgba(5,9,21,0) 70%)" }} />
      <div className="relative w-full max-w-md">
        {onBack && (
          <button data-testid="auth-back-home" onClick={onBack}
            className="inline-flex items-center gap-1.5 font-ui text-sm text-slate-500 hover:text-gold-300 transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>
        )}
        {/* Brand */}
        <div className="flex flex-col items-center text-center mb-8">
          <KlyrLogo className="w-16 h-11" />
          <div className="font-ui font-semibold tracking-[0.2em] text-xl text-gold-100 mt-3">KLYR
            <span className="block text-[10px] tracking-[0.5em] text-gold-400/70 mt-1">STUDIO</span>
          </div>
          <p className="font-read text-slate-400 text-sm mt-5 max-w-xs leading-relaxed">
            {mode === "forgot" ? "Forgot your password? We'll email you a secure link to choose a new one."
              : `${mode === "login" ? "Welcome back to the studio." : "Begin your book."} Start with 3 free previews — no card required.`}
          </p>
        </div>

        <div className="bg-navy-900/70 backdrop-blur-sm border border-gold-500/15 rounded-2xl p-7 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]">
          {mode !== "forgot" && <>
          <button
            data-testid="google-login-button"
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-3 font-ui text-sm font-medium bg-slate-100 hover:bg-white text-stone-900 rounded-xl px-4 py-3 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-gold-500/15" />
            <span className="font-ui text-[11px] uppercase tracking-widest text-slate-600">or</span>
            <div className="h-px flex-1 bg-gold-500/15" />
          </div>
          </>}

          {mode === "forgot" ? <ForgotPasswordForm onBack={() => setMode("login")} /> : (
          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  data-testid="auth-name-input"
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputCls}
                />
              </div>
            )}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="auth-email-input"
                type="email"
                required
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                data-testid="auth-password-input"
                type="password"
                required
                minLength={mode === "register" ? 8 : undefined}
                placeholder={mode === "register" ? "Create a password (8+ characters)" : "Password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </div>

            {error && (
              <p data-testid="auth-error" className="font-ui text-sm text-red-400">{error}</p>
            )}
            {mode === "login" && (
              <button type="button" data-testid="login-forgot-password-link" onClick={() => { setMode("forgot"); setError(""); }}
                className="font-ui text-xs text-gold-300 hover:text-gold-200 transition-colors">
                Forgot password?
              </button>
            )}

            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={busy}
              className="w-full gold-pill flex items-center justify-center gap-2 font-ui text-sm font-semibold disabled:opacity-50 rounded-xl px-4 py-3"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Sign in" : "Begin your book"}
            </button>
          </form>
          )}

          {mode !== "forgot" && <p className="text-center font-ui text-sm text-slate-400 mt-5">
            {mode === "login" ? "New to KLYR?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-gold-300 hover:text-gold-200 font-medium"
            >
              {mode === "login" ? "Create one free" : "Sign in"}
            </button>
          </p>}
        </div>

        <p className="flex items-center justify-center gap-2 font-ui text-xs text-slate-600 mt-6">
          <Sparkles className="w-3.5 h-3.5 text-gold-400/70" /> Powered by GPT · Claude · Gemini
        </p>
      </div>
    </div>
  );
};
